import { yupResolver } from "@hookform/resolvers/yup";
import { Form, Steps } from "antd";
import dayjs from "dayjs";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

import { NAMESPACES } from "@/constants/i18next.constants";
import { useAuthStore } from "@/features/Auth";
import { ONBOARDING_STATUSES } from "@/features/Auth";

import { STEPS } from "../../BillingOnboardingModal.constants";
import { useBillingOnboardingStore } from "../../BillingOnboardingModal.store";
import { defaultValues } from "./MultiStepForm.constants";
import {
  useCreateBilling,
  useGetBillingOnboardingData,
  useGetInstallationList,
} from "./MultiStepForm.hooks";
import {
  Container, FormContainer, StepsButtonsContainer, StepsContainer, StepTitle,
} from "./MultiStepForm.styles";
import { TBillingOnboardingForm, TMargin } from "./MultiStepForm.types";
import { validations } from "./MultiStepForm.validations";
import StepButtons from "./StepButtons";
import {
  FirstStep, FourthStep, SecondStep, ThirdStep,
} from "./Steps";

function MultiStepForm() {
  const { t } = useTranslation(NAMESPACES.BILLING_ONBOARDING);
  const { currentStep, setCurrentStep, setIsFormSuccess }
    = useBillingOnboardingStore(
      useShallow(state => ({
        currentStep: state.currentStep,
        setCurrentStep: state.setCurrentStep,
        setIsFormSuccess: state.setIsFormSuccess,
      })),
    );
  const setBillingOnboardingStatus = useAuthStore(
    state => state.setBillingOnboardingStatus,
  );
  const { mutate } = useCreateBilling();
  const { data: installationListData } = useGetInstallationList();
  const { data: billingOnboardingData } = useGetBillingOnboardingData();

  const currentStepValidation = validations[currentStep];
  const currentStepDefaultValue = defaultValues[currentStep];

  const { handleSubmit, control, trigger, watch } = useForm<TBillingOnboardingForm>({
    ...currentStepValidation && {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      resolver: yupResolver(validations[currentStep] as any),
      defaultValues: currentStepDefaultValue,
    },
  });

  // NOTES:
  // isOSb, vatExempt, etvExempt always will be false
  // accordPower: 0, setPower: 0 can set in update form or add form inside administration
  const onSubmit = (values: TBillingOnboardingForm) => {
    const installationReferenceObject = installationListData?.results.find(item =>
      item.value === values.installationReference);

    const payload = {
      name: values.name,
      sectorCode: values.sectorCode ?? "",
      paymentDayOffset: Number(values.paymentDayOffset),
      startTime: dayjs(values.startTime).format("YYYY-MM-DDTHH:mm:ss[Z]"),
      endTime: dayjs(values.endTime).format("YYYY-MM-DDTHH:mm:ss[Z]"),
      tariff: {
        isMediumVoltage: values.isMediumVoltage ?? false,
        isMultiTerm: values.isMultiTerm ?? false,
        isMultiTime: values.isMultiTime ?? false,
        isPrivate: values.isPrivate ?? false,
        isGreen: values.isGreen ?? false,
        isOSB: false,
        sector: values.sector ?? "",
      },
      margin:
          values.isPrivate
            ? {
              type: values.isFixedMargin
                ? "fixed"
                : "percentage",
              value: values.isFixedMargin
                ? Number(values.margin)
                : Number(values.margin) / 100,
            } as TMargin
            : undefined,
      meterSerialNumber: installationReferenceObject?.meterSerialNumber ?? "",
      etsoCode: installationReferenceObject?.etsoCode ?? "",
      vatExempt: false,
      etvExempt: false,
      accordPower: 0,
      setPower: 0,
    };

    mutate(
      payload,
      {
        onSuccess() {
          setBillingOnboardingStatus(ONBOARDING_STATUSES.COMPLETED);
          setIsFormSuccess(true);
        },
      },
    );
  };

  const nextStep = async () => {
    const isStepValid = await trigger();
    if (!isStepValid && currentStepValidation) {
      return;
    }
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  return (
    <Container>
      <StepsContainer>
        <Steps
          direction="vertical"
          current={currentStep}
          items={STEPS.map(step => ({
            title: t(step.titleKey),
          }))}
          style={{
            height: "100%",
          }}
        />
      </StepsContainer>
      <FormContainer>
        <Form
          name="billing-onboarding-form"
          onFinish={handleSubmit(onSubmit)}
          autoComplete="off"
          labelCol={{
            span: 7,
          }}
          wrapperCol={{
            span: 17,
          }}
          colon={false}
          labelAlign="left"
          layout="horizontal"
        >
          <StepTitle
            level={5}
            variant="medium"
            weight="semiBold"
            type="success"
          >
            {t(STEPS[currentStep].titleKey)}
          </StepTitle>
          {currentStep === 0 && (
            <FirstStep
              control={control}
              sectors={billingOnboardingData?.options.sectorData.sectors}
            />
          )}
          {currentStep === 1 && <SecondStep control={control} />}
          {currentStep === 2 && <ThirdStep control={control} watch={watch} />}
          {currentStep === 3 && (
            <FourthStep
              control={control}
              installationList={installationListData?.results}
            />
          )}
        </Form>
      </FormContainer>
      <StepsButtonsContainer>
        <StepButtons
          nextStep={nextStep}
          prevStep={prevStep}
          currentStep={currentStep}
        />
      </StepsButtonsContainer>
    </Container>
  );
}
export default MultiStepForm;
